import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Slider from 'react-slick';
import Section from '@latitude/section-rebrand';
import Modal from '../ModalRebrand/ModalRebrand';
import { BREAKPOINT, REBRAND_COLOR } from '@latitude/core/utils/constants';
import { List, ListItem } from '@latitude/list';
import { Accordion } from '@latitude/accordion-rebrand';
import Typography from '@latitude/typography';
import {
  mapFaqModal,
  resolveLoanColumn,
  slugifyTitle
} from './contentful/contentfulProductComparison.jsx';
import Button from '@latitude/button-rebrand';
import RadioBoxes from '../RadioBoxes/RadioBoxesV2/RadioBoxes';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';

// Slider / table switch points (px)
const TABLET_MAX_PX = 1100;
const MOBILE_MAX_PX = 768;
const TABLET_MAX = `${TABLET_MAX_PX}px`;
const DESKTOP_MIN = `${TABLET_MAX_PX + 1}px`;

// Renders nested lists. Items are strings or { text, children }, nested to any depth.
const renderNestedList = items => (
  <ul style={{ margin: 0, paddingLeft: '20px' }}>
    {items.map((item, index) =>
      typeof item === 'string' ? (
        <li key={index}>{item}</li>
      ) : (
        <li key={index}>
          {item.text}
          {item.children?.length > 0 && renderNestedList(item.children)}
        </li>
      )
    )}
  </ul>
);

// Asset Loan cards skip the LoanDetails <p> styling
const isAssetLoanColumn = column =>
  column?.heading?.trim().toLowerCase() === 'asset loan';

// ************ Fallback Data for Product Comparison Section ************

const FIXED_LOAN_ACCORDION_ITEMS_DATA = [
  {
    id: 'loan-standard-fees',
    title: 'Standard fees',
    content: [
      { color: REBRAND_COLOR.BLACK, text: 'Establishment fee' },
      { color: REBRAND_COLOR.BLACK_80, text: '__ESTABLISHMENT_FEE__' },
      { color: REBRAND_COLOR.BLACK, text: 'Loan service fee' },
      { color: REBRAND_COLOR.BLACK_80, text: '$16.50 per month' },
      { color: REBRAND_COLOR.BLACK, text: 'Direct debit fee' },
      { color: REBRAND_COLOR.BLACK_80, text: '$0' }
    ]
  },
  {
    id: 'loan-other-fees',
    title: 'Other fees (if applicable)',
    content: [
      { color: REBRAND_COLOR.BLACK, text: 'BPAY® payment handling fee' },
      { color: REBRAND_COLOR.BLACK_80, text: '$1.95' },
      { color: REBRAND_COLOR.BLACK, text: 'Late payment fee' },
      {
        color: REBRAND_COLOR.BLACK_80,
        text: '$45 if minimum repayment is not made by the agreed date'
      },
      { color: REBRAND_COLOR.BLACK, text: 'Early termination fee' },
      {
        color: REBRAND_COLOR.BLACK_80,
        text: '$500 (up until the final 3 months of loan term)'
      }
    ]
  },
  {
    id: 'loan-details',
    title: 'Loan details',
    content: [
      { color: REBRAND_COLOR.BLACK, text: 'Loan amount' },
      { color: REBRAND_COLOR.BLACK_80, text: 'From $5,000' },
      { color: REBRAND_COLOR.BLACK, text: 'Repayment frequency' },
      {
        color: REBRAND_COLOR.BLACK_80,
        text: 'Weekly, fortnightly, or monthly'
      },
      { color: REBRAND_COLOR.BLACK, text: 'Loan terms' },
      {
        color: REBRAND_COLOR.BLACK_80,
        text: renderNestedList(['2 to 7 years', 'Secured or unsecured'])
      }
    ]
  }
];

const VARIABLE_LOAN_ACCORDION_ITEMS_DATA = [
  {
    id: 'loan-standard-fees',
    title: 'Standard fees',
    content: [
      { color: REBRAND_COLOR.BLACK, text: 'Establishment fee' },
      { color: REBRAND_COLOR.BLACK_80, text: '__ESTABLISHMENT_FEE__' },
      { color: REBRAND_COLOR.BLACK, text: 'Loan service fee' },
      { color: REBRAND_COLOR.BLACK_80, text: '$16.50 per month' },
      { color: REBRAND_COLOR.BLACK, text: 'Direct debit fee' },
      { color: REBRAND_COLOR.BLACK_80, text: '$0' }
    ]
  },
  {
    id: 'loan-other-fees',
    title: 'Other fees (if applicable)',
    content: [
      { color: REBRAND_COLOR.BLACK, text: 'BPAY® payment handling fee' },
      { color: REBRAND_COLOR.BLACK_80, text: '$1.95' },
      { color: REBRAND_COLOR.BLACK, text: 'Late payment fee' },
      {
        color: REBRAND_COLOR.BLACK_80,
        text: '$45 if minimum repayment is not made by the agreed date'
      },
      { color: REBRAND_COLOR.BLACK, text: 'Early termination fee' },
      { color: REBRAND_COLOR.BLACK_80, text: '$0' }
    ]
  },
  {
    id: 'loan-details',
    title: 'Loan details',
    content: [
      { color: REBRAND_COLOR.BLACK, text: 'Loan amount' },
      { color: REBRAND_COLOR.BLACK_80, text: 'From $5,000' },
      { color: REBRAND_COLOR.BLACK, text: 'Repayment frequency' },
      {
        color: REBRAND_COLOR.BLACK_80,
        text: 'Weekly, fortnightly, or monthly'
      },
      { color: REBRAND_COLOR.BLACK, text: 'Loan terms' },
      {
        color: REBRAND_COLOR.BLACK_80,
        text: renderNestedList(['2 to 7 years', 'Secured or unsecured'])
      }
    ]
  }
];

const LOAN_COLUMNS_DATA = [
  {
    id: 'fixed',
    heading: 'Secured Car Loan',
    interestRateFrom: 9.99,
    interestRateTo: 29.39,
    benefits: [
      'Use your vehicle as security for your loan',
      'Suitable if you’ve already found your vehicle'
    ],
    explanation: {
      title: 'Fixed',
      body:
        'A fixed rate makes budgeting easier. It’s ideal if you want certainty and peace of mind. Your interest rate stays the same, no surprises, no changes.'
    },
    explanationTitle2: 'Variable',
    explainationBodyText2: {
      data: {},
      content: [
        {
          data: {},
          content: [
            {
              data: {},
              marks: [],
              value:
                'A variable rate offers flexibility. Ideal if you want to pay off your loan early with fewer restrictions.',
              nodeType: 'text'
            }
          ],
          nodeType: 'paragraph'
        }
      ],
      nodeType: 'document'
    },
    additionalExplanationTitle: 'Why choose a secured loan?',
    additionalExplanationBodyText: {
      data: {},
      content: [
        {
          data: {},
          content: [
            {
              data: {},
              marks: [],
              value:
                "Get the benefit of a lower rate if you're willing to secure your loan. May help reduce the overall cost of borrowing. ",
              nodeType: 'text'
            }
          ],
          nodeType: 'paragraph'
        }
      ],
      nodeType: 'document'
    },
    button: {
      text: 'Get personalised rate',
      url: 'https://loans.portal.latitudefinancial.com.au/s/quote'
    },
    accordionItemsData: FIXED_LOAN_ACCORDION_ITEMS_DATA
  },
  {
    id: 'variable',
    heading: 'Unsecured Car Loan',
    interestRateFrom: '9.99',
    interestRateTo: 29.39,
    benefits: [
      'No need to use your car or another asset as security',
      'Enjoy more flexibility in your borrowing'
    ],
    explanation: {
      title: 'Fixed',
      body:
        'A fixed rate makes budgeting easier. It’s ideal if you want certainty and peace of mind. Your interest rate stays the same, no surprises, no changes.'
    },
    explanationTitle2: 'Variable',
    explainationBodyText2: {
      data: {},
      content: [
        {
          data: {},
          content: [
            {
              data: {},
              marks: [],
              value:
                'A variable rate offers flexibility. Ideal if you want to pay off your loan early with fewer restrictions.',
              nodeType: 'text'
            }
          ],
          nodeType: 'paragraph'
        }
      ],
      nodeType: 'document'
    },
    additionalExplanationTitle: 'Why choose an unsecured loan?',
    additionalExplanationBodyText: {
      data: {},
      content: [
        {
          data: {},
          content: [
            {
              data: {},
              marks: [],
              value:
                "Allows you to shop around with confidence if you haven't chosen your vehicle yet.",
              nodeType: 'text'
            }
          ],
          nodeType: 'paragraph'
        }
      ],
      nodeType: 'document'
    },

    button: {
      text: 'Get personalised rate',
      url: 'https://loans.portal.latitudefinancial.com.au/s/quote'
    },
    accordionItemsData: VARIABLE_LOAN_ACCORDION_ITEMS_DATA
  }
];

const DEFAULT_FIXED_COMPARISON_RATE_FROM = 11.66;
const DEFAULT_FIXED_COMPARISON_RATE_TO = 30.97;
const DEFAULT_VARIABLE_COMPARISON_RATE_FROM = 11.66;
const DEFAULT_VARIABLE_COMPARISON_RATE_TO = 30.97;
const DEFAULT_FAQ_HEADING = 'Frequently asked questions';
const DEFAULT_FAQ_TITLE =
  'Compare our fixed and variable loan options to find the right fit';
const DEFAULT_FAQ_ITEMS = [
  {
    label: 'How is my interest rate determined?',
    href: '/how-interest-rates-are-determined/'
  },
  {
    label: 'What is a comparison rate?',
    title: 'What does the comparison rate mean on a personal loan?',
    content: (
      <>
        <Typography.Body color={REBRAND_COLOR.BLACK_80}>
          A comparison rate in the context of personal loans is a way of
          presenting the total cost of a loan to borrowers. It includes both the
          nominal interest rate and any associated fees or charges over the life
          of the loan, expressed as an annual percentage rate (APR). This allows
          borrowers to make more informed decisions when comparing different
          loan options from various lenders.
        </Typography.Body>
        <br />
        <Typography.Body color={REBRAND_COLOR.BLACK_80}>
          The comparison rate takes into account not only the interest rate but
          also any upfront or ongoing fees, such as application fees, annual
          fees, and other charges. By including these additional costs, the
          comparison rate provides a more accurate representation of the true
          cost of borrowing.
        </Typography.Body>
        <br />
        <Typography.Body color={REBRAND_COLOR.BLACK_80}>
          When evaluating personal loan options, it's important to consider both
          the nominal interest rate and the comparison rate. While the nominal
          interest rate indicates the cost of borrowing in terms of interest
          alone, the comparison rate gives you a more comprehensive view of the
          overall cost, helping you choose the loan that best suits your
          financial needs.
        </Typography.Body>
      </>
    )
  },
  {
    label: 'What is redraw?',
    title: 'What is redraw?',
    content: (
      <Typography.Body color={REBRAND_COLOR.BLACK_80}>
        Redraw is a feature of Latitude Variable Rate Personal Loans that gives
        access to additional repayments you've made on your loan. There's no
        cost to redraw and no need to reapply for a new loan. Just flexible
        access to funds when you need them. Call 1300 973 422 to see if you are
        eligible.
      </Typography.Body>
    )
  },
  {
    label: 'Secured vs Unsecured loan?',
    title: 'Secured vs Unsecured loan?',
    content: (
      <Typography.Body color={REBRAND_COLOR.BLACK_80}>
        When shopping around or applying for a personal loan, one decision
        you'll need to make is whether you want a secured or unsecured loan.
        Taking out a secured loan means you're able to provide an asset like
        your car, for the lender to secure your loan against. An advantage of
        taking out a secured loan is that you may be eligible for a lower
        interest rate. If you don't have a suitable asset to secure your loan
        against, you may decide to apply for an unsecured loan.
      </Typography.Body>
    )
  }
];

const getFirstDefinedValue = (...values) =>
  values.find(value => value !== undefined && value !== null && value !== '');

function ProductComparisonSection({
  id,
  contentfulID,
  heading = 'Compare our fixed and variable loan options to find the right fit',
  establishmentFee = '$395',
  fixedComparisonRateFrom,
  fixedComparisonRateTo,
  variableComparisonRateFrom,
  variableComparisonRateTo,
  loanColumns: loanColumnsData,
  faq
}) {
  const [activeModalSlug, setActiveModalSlug] = useState(null);
  const [groupActiveIndex, setGroupActiveIndex] = useState(0);
  const [isVariable, setIsVariable] = useState({});
  const comparisonSectionRef = useRef(null);

  function formatRates(
    fromRate,
    toRate,
    rateColour = '#000',
    footnoteIcon = '^',
    isMobile = false
  ) {
    return (
      <div
        css={`
          width: 100%;
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: baseline;
          padding: 12px 0;
          flex-wrap: wrap;
        `}
      >
        <div
          css={{
            color: REBRAND_COLOR.MID_BLUE,
            whiteSpace: 'nowrap',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'baseline'
          }}
        >
          {isMobile ? (
            <Typography.SH1 semibold color={REBRAND_COLOR.MID_BLUE}>
              {fromRate}
            </Typography.SH1>
          ) : (
            <Typography.N4 color={REBRAND_COLOR.MID_BLUE}>
              {fromRate}
            </Typography.N4>
          )}
          <div>
            <Typography.Body color={REBRAND_COLOR.MID_BLUE}>
              %p.a.<sup>{footnoteIcon}</sup>
            </Typography.Body>
          </div>
        </div>

        <div css={{ flex: '2 2 0', textAlign: 'center' }}>
          <Typography.Body>to</Typography.Body>
        </div>

        <div
          css={{
            color: rateColour,
            whiteSpace: 'nowrap',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'baseline'
          }}
        >
          {isMobile ? (
            <Typography.SH1 semibold color={REBRAND_COLOR.MID_BLUE}>
              {toRate}
            </Typography.SH1>
          ) : (
            <Typography.N4 color={REBRAND_COLOR.MID_BLUE}>
              {toRate}
            </Typography.N4>
          )}
          <div>
            <Typography.Body color={REBRAND_COLOR.MID_BLUE}>
              %p.a.<sup>{footnoteIcon}</sup>
            </Typography.Body>
          </div>
        </div>
      </div>
    );
  }

  function onFaqClick(event, item) {
    if (item.title && item.content) {
      event.preventDefault();
      setActiveModalSlug(slugifyTitle(item.title));
    }
  }
  const faqItemsFromCms = Array.isArray(faq?.faqItems) ? faq.faqItems : [];
  const faqHeading = faq?.title || DEFAULT_FAQ_HEADING;
  const faqTitle = faq?.heading || DEFAULT_FAQ_TITLE;

  const faqItems =
    faqItemsFromCms.length > 0 ? faqItemsFromCms : DEFAULT_FAQ_ITEMS;

  const faqModals = faqItems
    ?.filter(item => item.title && item.content)
    ?.map(mapFaqModal);

  const baseLoanColumns =
    Array.isArray(loanColumnsData) && loanColumnsData.length > 0
      ? loanColumnsData
      : LOAN_COLUMNS_DATA;

  const resolvedLoanColumns = baseLoanColumns?.map(column =>
    resolveLoanColumn(column, {
      establishmentFee,
      LoanDetailsComponent: LoanDetails
    })
  );

  // With 3+ cards the slider is used at every width; with 2 cards the
  // side-by-side table is used above TABLET_MAX_PX.
  const columnCount = resolvedLoanColumns?.length ?? 0;
  const useSliderOnDesktop = columnCount >= 3;

  useEffect(() => {
    const groups = comparisonSectionRef.current?.querySelectorAll(
      '.comparison-card-alignment-group'
    );
    const observers = [];

    groups?.forEach(group => {
      const sections = Array.from(
        group.querySelectorAll('[data-comparison-section]')
      );
      const sectionGroups = sections.reduce((result, section) => {
        const name = section.dataset.comparisonSection;
        result[name] = [...(result[name] || []), section];
        return result;
      }, {});

      Object.values(sectionGroups).forEach(groupSections => {
        const updateHeight = () => {
          groupSections.forEach(section => {
            section.style.boxSizing = 'border-box';
            section.style.minHeight = '0px';
          });
          const maxHeight = Math.max(
            ...groupSections.map(section =>
              section.getBoundingClientRect().height
            )
          );

          groupSections.forEach(section => {
            section.style.minHeight = `${maxHeight}px`;
          });
        };

        if (typeof ResizeObserver !== 'undefined') {
          const observer = new ResizeObserver(updateHeight);
          groupSections.forEach(section => {
            observer.observe(section);
            if (section.parentElement) {
              observer.observe(section.parentElement);
            }
          });
          observers.push(observer);
        }

        updateHeight();
      });
    });

    return () => observers.forEach(observer => observer.disconnect());
  }, [columnCount, establishmentFee, isVariable, loanColumnsData]);

  const onChangeRepaymentType = (columnId, event) => {
    const value = event.target.value === 'true';

    setIsVariable(prev => ({
      ...prev,
      [columnId]: value
    }));
  };

  const faqs = (
    <>
      <div css={{ marginBottom: '15px' }}>
        <Typography.SH3>{faqTitle}</Typography.SH3>
        <br />
        <Typography.SH4>{faqHeading}</Typography.SH4>
      </div>
      {faqItems?.map(item => (
        <div
          key={`${item.label}-${item.href || item.title}`}
          css={`
            margin-bottom: 15px;
            a {
              border-color: ${REBRAND_COLOR.INDIGO} !important;
            }
          `}
        >
          <Typography.LinkLarge
            color={REBRAND_COLOR.INDIGO}
            href={item.href || ''}
            onClick={event => onFaqClick(event, item)}
            trackEventData={{
              // category: CALCULATOR_TRACKING.CATEGORY,
              // action: CALCULATOR_TRACKING.ACTION,
              label: item.label
            }}
          >
            {item.label}
          </Typography.LinkLarge>
        </div>
      ))}
    </>
  );

  const comparisonTable = (
    <ComparisonCards
      className="comparison-card-alignment-group"
      resolvedLoanColumns={resolvedLoanColumns?.length}
    >
      {resolvedLoanColumns?.map(column => (
        <ComparisonCard
          key={column.id}
          resolvedLoanColumns={resolvedLoanColumns?.length}
        >
          <CardHeader data-comparison-section="heading">
            <Typography.SH3 semibold>{column.heading}</Typography.SH3>
          </CardHeader>

          <CardSection data-comparison-section="benefits">
            <List css={{ marginTop: '24px' }}>
              {column.benefits.map((item, index) => (
                <ListItem key={`${column.id}-benefit-${index}`}>
                  <Typography.Body>{item}</Typography.Body>
                </ListItem>
              ))}
            </List>
          </CardSection>
          <CardSection data-comparison-section="button">
            {column?.button?.url && (
              <Button
                variant="primary"
                size="md"
                ctaUrl={column?.button?.url}
                ctaText={column?.button?.text}
                target="_self"
                trackId={'product-comparison-section'}
                trackEventData={{
                  category: 'cta',
                  action: 'quote-link',
                  label: column?.button?.text,
                  location: `product-comparison-section-${column.heading}`,
                  value: column?.button?.text
                }}
                trackProductId={['PLAULF-WEB']}
              />
            )}
          </CardSection>
          <CardSection data-comparison-section="additional-explanation">
            <Typography.CTALarge>
              {column?.additionalExplanationTitle}
            </Typography.CTALarge>

            <Typography.Body>
              {documentToReactComponents(column?.additionalExplanationBodyText)}
            </Typography.Body>
            <div style={{ height: '24px' }}> </div>
          </CardSection>
          <CardSection data-comparison-section="repayment-type">
            <StyledRadioBoxes
              id={`repayment-type-comparision-${column.id}`}
              name={`repaymentsTypeComparison-${column.id}`}
              onChange={event => onChangeRepaymentType(column.id, event)}
              values={[
                { value: false, text: column?.explanation?.title },
                { value: true, text: column?.explanationTitle2 }
              ]}
              selectedValue={isVariable[column.id] ?? false}
            />
          </CardSection>
          <CardSection
            data-comparison-section="repayment-explanation"
            marginTop="16px"
          >
            <div
              css={{
                background: '#E5EEFE',
                borderLeft: '3px solid #0156f5',
                borderRadius: '0 0 32px 0',
                padding: '12px 16px'
              }}
            >
              <Typography.Body>
                {isVariable[column.id]
                  ? documentToReactComponents(column?.explainationBodyText2)
                  : column?.explanation?.body}
              </Typography.Body>
            </div>
          </CardSection>

          <CardSection data-comparison-section="rates" marginTop="16px">
            <Typography.Body>Interest rate from</Typography.Body>

            {formatRates(
              isVariable[column.id]
                ? column.interestRateFromVariable ?? column.interestRateFrom
                : column.interestRateFrom,
              isVariable[column.id]
                ? column.interestRateToVariable ?? column.interestRateTo
                : column.interestRateTo,
              column.rateColor,
              '^'
            )}

            <HorizLine />

            <Typography.Body>Comparison rate from</Typography.Body>

            {formatRates(
              isVariable[column.id]
                ? column.comparisonRateFromVariable ?? column.comparisonRateFrom
                : column.comparisonRateFrom,
              isVariable[column.id]
                ? column.comparisonRateToVariable ?? column.comparisonRateTo
                : column.comparisonRateTo,
              column.rateColor,
              '*'
            )}
          </CardSection>

          <CardSection
            className={`accordion-wrap${
              isAssetLoanColumn(column) ? ' asset-loan' : ''
            }`}
          >
            {isVariable[column.id] ? (
              <Accordion
                {...column.accordionProps}
                borderedChildren
                hasSmoothScroll={false}
                groupActiveIndex={groupActiveIndex}
                setGroupActiveIndex={setGroupActiveIndex}
                items={column.accordionItemsVariable}
              />
            ) : (
              <Accordion
                {...column.accordionProps}
                borderedChildren
                hasSmoothScroll={false}
                groupActiveIndex={groupActiveIndex}
                setGroupActiveIndex={setGroupActiveIndex}
                items={column.accordionItems}
              />
            )}
          </CardSection>
        </ComparisonCard>
      ))}
    </ComparisonCards>
  );

  const mobileComparisonSlider = (
    <ComparisonSlider
      className="comparison-card-alignment-group"
      dots
      arrows={false}
      infinite={false}
      slidesToShow={useSliderOnDesktop ? 3 : 2}
      slidesToScroll={1}
      $hideDotsOnDesktop={columnCount <= 3}
      responsive={[
        { breakpoint: TABLET_MAX_PX, settings: { slidesToShow: 2 } },
        { breakpoint: MOBILE_MAX_PX, settings: { slidesToShow: 1.1 } }
      ]}
    >
      {resolvedLoanColumns?.map(column => (
        <div key={column.id}>
          <MobileComparisonCard
            resolvedLoanColumns={resolvedLoanColumns?.length}
          >
            <CardHeader data-comparison-section="heading">
              <Typography.SH3 semibold>{column.heading}</Typography.SH3>
            </CardHeader>

            <CardSection data-comparison-section="benefits">
              <List css={{ marginTop: '24px' }}>
                {column.benefits.map((item, index) => (
                  <ListItem key={`${column.id}-benefit-${index}`}>
                    <Typography.Body>{item}</Typography.Body>
                  </ListItem>
                ))}
              </List>
            </CardSection>
            <CardSection data-comparison-section="button">
              {column?.button?.url && (
                <Button
                  variant="primary"
                  size="md"
                  ctaUrl={column?.button?.url}
                  ctaText={column?.button?.text}
                  target="_self"
                  trackId={'product-comparison-section'}
                  trackEventData={{
                    category: 'cta',
                    action: 'quote-link',
                    label: column?.button?.text,
                    location: `product-comparison-section-${column.heading}`,
                    value: column?.button?.text
                  }}
                  trackProductId={['PLAULF-WEB']}
                />
              )}
            </CardSection>
            <CardSection data-comparison-section="additional-explanation">
              <Typography.CTALarge>
                {column?.additionalExplanationTitle}
              </Typography.CTALarge>

              <Typography.Body>
                {documentToReactComponents(
                  column?.additionalExplanationBodyText
                )}
              </Typography.Body>
              <div style={{ height: '24px' }}> </div>
            </CardSection>
            <CardSection data-comparison-section="repayment-type">
              <StyledRadioBoxes
                id={`repayment-type-comparision-${column.id}`}
                name={`repaymentsTypeComparison-${column.id}`}
                onChange={event => onChangeRepaymentType(column.id, event)}
                values={[
                  { value: false, text: column?.explanation?.title },
                  { value: true, text: column?.explanationTitle2 }
                ]}
                selectedValue={isVariable[column.id] ?? false}
              />
            </CardSection>
            <CardSection
              data-comparison-section="repayment-explanation"
              marginTop="16px"
            >
              <div
                css={{
                  background: '#E5EEFE',
                  borderLeft: '3px solid #0156f5',
                  borderRadius: '0 0 32px 0',
                  padding: '12px 16px'
                }}
              >
                <Typography.Body>
                  {isVariable[column.id]
                    ? documentToReactComponents(column?.explainationBodyText2)
                    : column?.explanation?.body}
                </Typography.Body>
              </div>
            </CardSection>

            <CardSection data-comparison-section="rates" marginTop="16px">
              <Typography.Body>Interest rate from</Typography.Body>

              {formatRates(
                isVariable[column.id]
                  ? column.interestRateFromVariable ?? column.interestRateFrom
                  : column.interestRateFrom,
                isVariable[column.id]
                  ? column.interestRateToVariable ?? column.interestRateTo
                  : column.interestRateTo,
                column.rateColor,
                '^',
                true
              )}

              <HorizLine />

              <Typography.Body>Comparison rate from</Typography.Body>

              {formatRates(
                isVariable[column.id]
                  ? column.comparisonRateFromVariable ??
                      column.comparisonRateFrom
                  : column.comparisonRateFrom,
                isVariable[column.id]
                  ? column.comparisonRateToVariable ?? column.comparisonRateTo
                  : column.comparisonRateTo,
                column.rateColor,
                '*',
                true
              )}
            </CardSection>

            <CardSection
              className={`accordion-wrap${
                isAssetLoanColumn(column) ? ' asset-loan' : ''
              }`}
            >
              {isVariable[column.id] ? (
                <Accordion
                  {...column.accordionProps}
                  borderedChildren
                  hasSmoothScroll={false}
                  groupActiveIndex={groupActiveIndex}
                  setGroupActiveIndex={setGroupActiveIndex}
                  items={column.accordionItemsVariable}
                />
              ) : (
                <Accordion
                  {...column.accordionProps}
                  borderedChildren
                  hasSmoothScroll={false}
                  groupActiveIndex={groupActiveIndex}
                  setGroupActiveIndex={setGroupActiveIndex}
                  items={column.accordionItems}
                />
              )}
            </CardSection>
          </MobileComparisonCard>
        </div>
      ))}
    </ComparisonSlider>
  );

  return (
    <Wrapper ref={comparisonSectionRef}>
      <Section
        id={'compare-products'}
        data-contentful={contentfulID}
        css={{ paddingTop: '32px', paddingBottom: '32px' }}
      >
        <Typography.SH1>{heading}</Typography.SH1>
        {faqItemsFromCms.length > 0 && (
          <div>
            <br />
            {faqs}
          </div>
        )}

        <div className="row">
          <div className="pt-4 mt-2 col-12">
            <DesktopComparison useSliderOnDesktop={useSliderOnDesktop}>
              {comparisonTable}
            </DesktopComparison>

            <MobileComparison useSliderOnDesktop={useSliderOnDesktop}>
              {mobileComparisonSlider}
            </MobileComparison>
          </div>
        </div>
        {faqModals.map(({ id: modalSlug, title, content }) => (
          <Modal
            key={modalSlug}
            isOpen={activeModalSlug === modalSlug}
            onRequestClose={() => setActiveModalSlug(null)}
            title={
              <Typography.SH4 semibold color={REBRAND_COLOR.BLACK}>
                {title}
              </Typography.SH4>
            }
            content={content}
            ariaHideApp={false}
          />
        ))}
      </Section>
    </Wrapper>
  );
}

// Table: shown above TABLET_MAX only when there are fewer than 3 cards.
const DesktopComparison = styled.div`
  display: ${({ useSliderOnDesktop }) =>
    useSliderOnDesktop ? 'none' : 'block'};

  @media (max-width: ${TABLET_MAX}) {
    display: none;
  }
`;

// Slider: always shown with 3+ cards, otherwise only at TABLET_MAX and below.
const MobileComparison = styled.div`
  display: ${({ useSliderOnDesktop }) =>
    useSliderOnDesktop ? 'block' : 'none'};

  @media (max-width: ${TABLET_MAX}) {
    display: block;
  }
`;

const ComparisonSlider = styled(Slider)`
  margin: 0 -16px 0 0;

  @media (min-width: ${DESKTOP_MIN}) {
    margin: 0;

    .slick-dots {
      display: ${({ $hideDotsOnDesktop }) =>
        $hideDotsOnDesktop ? 'none' : 'block'};
    }
  }

  .slick-list {
    margin: 0 -8px;
  }

  .slick-slide {
    padding: 0 8px;
    box-sizing: border-box;
    height: auto !important;
    float: none !important; /* slick floats slides by default */
  }
  .slick-track {
    display: flex !important;
  }

  .slick-slide > div {
    height: 100%;
  }
  .slick-slide > div > div {
    height: 100%;
  }

  .slick-dots {
    position: relative;
    bottom: auto;
    margin-top: 16px;
  }

  .slick-dots li {
    width: 12px;
    height: 12px;
    margin: 0 4px;
  }

  .slick-dots li button {
    width: 12px;
    height: 12px;
    padding: 0;
  }

  .slick-dots li button:before {
    content: '';
    display: block;
    width: 12px;
    height: 12px;
    background-color: ${REBRAND_COLOR.WHITE};
    border-radius: 50%;
    opacity: 1;
  }

  .slick-dots li.slick-active {
    width: 24px;
  }

  .slick-dots li.slick-active button {
    width: 24px;
  }

  .slick-dots li.slick-active button:before {
    width: 24px;
    height: 12px;
    background-color: ${REBRAND_COLOR.GREEN};
    border-radius: 32px;
  }
`;

const MobileComparisonCard = styled.div`
  width: 100%;
  box-sizing: border-box;
  background: ${REBRAND_COLOR.WHITE};
  padding: 24px 20px;
  border-radius: 12.663px;
  height: 100%;
`;

const Wrapper = styled.div`
  background-color: ${REBRAND_COLOR.STONE};
  .typography-base__body p {
    margin: 0;
  }
  .accordion p,
  .accordion span,
  .accordion li {
    color: ${REBRAND_COLOR.INDIGO} !important;
  }
  .accordion__item {
    margin-bottom: 24px;
    &:last-child {
      margin-bottom: 0;
    }
  }
  .accordion__item .link-block.bg-stone {
    background-color: ${REBRAND_COLOR.WHITE} !important;
    border-top: 0.5px solid #d9d9d9;
    border-bottom: 0.5px solid #d9d9d9;
  }
  .accordion__item .bg-black-10 {
    background-color: ${REBRAND_COLOR.WHITE} !important;
  }
  .accordion svg {
    color: ${REBRAND_COLOR.MID_BLUE};
  }
  .accordion .bg-stone span {
    font-size: 18px;
    font-weight: 500;
  }
  .accordion [class*='ProductComparisionSectionRebrand__LoanDetails'] {
    font-size: 18px;
    gap: 0px;
    padding: 24px 15px 0px 15px;
    li {
      margin-bottom: 0px;
    }
    ul ul {
      margin-top: 4px;
      list-style-type: circle;
    }
    ul ul ul {
      list-style-type: square;
    }
  }
  .accordion-wrap:not(.asset-loan)
    .accordion
    [class*='ProductComparisionSectionRebrand__LoanDetails']
    p {
    &:nth-child(odd) {
      font-weight: 600;
    }

    &:nth-child(2n) {
      font-weight: 500;
      margin-bottom: 24px !important;
    }

    &:last-child {
      margin-bottom: 0 !important;
    }
  }
`;

const LoanDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding: 10px 15px;
`;

const HorizLine = styled.hr`
  border: 0.5px solid #d9d9d9;
`;

// The table is hidden at TABLET_MAX and below, so no display:block fallback is needed.
const ComparisonCards = styled.div`
  display: flex;
  gap: ${({ resolvedLoanColumns }) =>
    resolvedLoanColumns >= 3 ? '16px' : '48px'};
  width: 100%;
`;

const ComparisonCard = styled.div`
  flex: 1;
  min-width: 0;
  background: ${REBRAND_COLOR.WHITE};
  padding: 48px 48px 16px 48px;
  box-sizing: border-box;
  @media (max-width: ${BREAKPOINT.MD}) {
    padding: 48px 16px 16px 16px;
  }
  border-radius: 12.663px;
  ${({ resolvedLoanColumns }) =>
    resolvedLoanColumns >= 3 &&
    `
      .typography-base__n4 {
        font-size: 32px;
      }
    `}
`;

const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const CardSection = styled.div`
  margin-top: ${({ marginTop = '24px' }) => marginTop};
  li {
    margin-bottom: 14px;
    @media (max-width: ${BREAKPOINT.MD}) {
      margin-bottom: 11px;
    }
  }

  li::before {
    color: ${REBRAND_COLOR.INDIGO};
  }
`;

const StyledRadioBoxes = styled(RadioBoxes)`
  border: 2px solid ${REBRAND_COLOR.INDIGO};
  border-radius: 4px;
  padding: 5px;
  height: 48px;
  && label.lfs-radio-label {
    border: none;
    border-radius: 4px;
  }
  && .radio-box__text,
  && .radio-box__input:checked + .radio-box__text {
    color: ${REBRAND_COLOR.INDIGO} !important;
  }
  && .radio-box__label.active,
  && .radio-box__input:checked + .radio-box__text {
    background-color: ${REBRAND_COLOR.MID_BLUE} !important;
    color: ${REBRAND_COLOR.WHITE} !important;
  }
  && .radio-box__label.active span {
    color: ${REBRAND_COLOR.WHITE} !important;
  }

  && .input-wrap label {
    margin-bottom: 0px;
  }
`;

export default ProductComparisonSection;
